import { z } from 'zod';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { KeyTakeawayHighlightType } from 'libs/end-to-end/src/key-takeaways';
import { TreatmentRecommendation } from 'libs/end-to-end/src/treatment';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { FocusAreaEnum } from 'libs/end-to-end/src/focus-area';
import { BiasType } from 'libs/end-to-end/src/bias-type';
import { TestCount } from 'libs/end-to-end/src/tests-utils';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';

export const InsightHighlight = z
  .object({ percentage: z.number(), type: z.string() })
  .describe('InsightHighlight');

export type InsightHighlight = z.infer<typeof InsightHighlight>;

export const Insight = z
  .object({
    /**
     * Bias, parity, representation, etc.
     */
    type: KeyTakeawayHighlightType,
    /**
     * Used for generating text like 80% difference, etc.
     * @example 80% difference, etc.
     */
    highlight: InsightHighlight.nullable(),
    confidencePercentage: z.number().nullable(),
    /**
     * Bar or bin scatter data. We need the explanation to have some text to display as a paragraph.
     */
    groupResults: RootCauseGroupResult.array(),
    /**
     * @example MostImpact, etc.
     */
    recommendations: TreatmentRecommendation.array().nullable(),
    dimension: z.string(),
    metric: z.string(),
    referenceLevel: z.string(),
    treatmentLevel: z.string(),
    totalTestCount: TestCount.nullable(),
    biasTypes: BiasType.array(),
    /**
     * Importance of the insight. Used for ranking all the insights.
     */
    priority: z.number(),
  })
  .describe('Insight');

export type Insight = z.infer<typeof Insight>;

export const InsightsResult = z
  .object({
    [FocusAreaEnum.Values.Compensation]: Insight.array().optional(),
    [FocusAreaEnum.Values.Hiring]: Insight.array().optional(),
    [FocusAreaEnum.Values.Attrition]: Insight.array().optional(),
    [FocusAreaEnum.Values.Promotion]: Insight.array().optional(),
    [FocusAreaEnum.Values.Engagement]: Insight.array().optional(),
    [FocusAreaEnum.Values.Performance]: Insight.array().optional(),
    [FocusAreaEnum.Values.Leadership]: Insight.array().optional(),
  })
  .describe('InsightsResult');

export type InsightsResult = z.infer<typeof InsightsResult>;

export interface InsightWithFocusArea {
  insight: Insight;
  focusArea: MyFocusAreaDefinition;
  metricDefinition: MyMetricDefinition;
}

export const InsightsFilterInput = z
  .object({
    dimensions: z.string().array().nullable(),
    focusArea: FocusArea,
  })
  .describe('InsightsFilterInput');

export type InsightsFilterInput = z.infer<typeof InsightsFilterInput>;

export const getShowBeforeEmpsOrAppsOrDefault = (
  showBeforeEmpsOrApps: boolean | undefined,
) => showBeforeEmpsOrApps ?? false;
export const getInsightFormatOrDefault = (insightFormat: string | undefined) =>
  insightFormat ?? '$level';
export const getInsightWithReferenceFormatOrDefault = (
  insightWithReferenceFormat: string | undefined,
) => insightWithReferenceFormat ?? '$level and $reference';

export const InsightV2 = z
  .object({
    rawDisparity: z.number().nullable(),
    controlledDisparity: z.number().nullable(),
    debiasedDisparity: z.number().nullable(),
    confidence: z.number().nullable(),
    focusArea: FocusArea,
    metric: z.string(),
    dimension: z.string(),
    referenceLevel: z.string(),
    treatmentLevel: z.string(),
  })
  .describe('InsightV2');

export type InsightV2 = z.infer<typeof InsightV2>;

export const InsightsV2 = z.array(InsightV2);

export type InsightsV2 = z.infer<typeof InsightsV2>;
